import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sprout, AtSign, Check, X, Mail } from "lucide-react";

const USERNAME_RE = /^[a-z0-9_]{3,30}$/;

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const marketEntry = searchParams.get("entry") === "market";
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const normalized = username.trim().toLowerCase();
  const usernameValid = USERNAME_RE.test(normalized);
  const showUsernameHint = username.length > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!usernameValid) {
      setErr("Username must be 3-30 characters: lowercase letters, numbers, underscores only.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await register(normalized, password, name, email);
      nav(marketEntry ? "/dashboard" : "/assessment", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-app grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[var(--cm-secondary)] text-white relative overflow-hidden">
        <Link to="/" className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-full bg-white/15 grid place-items-center">
            <Sprout className="h-4 w-4" />
          </span>
          <span className="font-display font-bold text-xl">Bahoi <span className="opacity-70">· causematch</span></span>
        </Link>
        <div>
          <h2 className="font-display font-bold text-4xl leading-tight tracking-tight max-w-md">
            Time is the most honest currency we have.
          </h2>
          <p className="text-white/70 mt-6 max-w-md" data-testid="register-side-copy">
            {marketEntry ? (
              <>
                Pick a username, share your email, and you&apos;re in. Post what you need, pick
                up what you can, and offer or redeem goods and services — all inside a{" "}
                <span className="font-semibold text-white">bahoi</span>, a supportive community
                where time is the currency.
              </>
            ) : (
              <>
                Pick a username, share your email, and take the 4-minute assessment. We&apos;ll match you with causes that
                align with how you actually see the world — and welcome you into a{" "}
                <span className="font-semibold text-white">bahoi</span>, a supportive community where
                time is the currency.
              </>
            )}
          </p>
        </div>
        <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-[var(--cm-accent)]/30 blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="md:hidden flex items-center gap-2 mb-8">
            <span className="h-9 w-9 rounded-full bg-[var(--cm-secondary)] grid place-items-center text-white">
              <Sprout className="h-4 w-4" />
            </span>
            <span className="font-display font-bold text-xl">Bahoi <span className="opacity-70">· causematch</span></span>
          </Link>
          <span className="overline text-[var(--cm-primary)]">Create an account</span>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-2 tracking-tight">
            Start with 2 free hours.
          </h1>
          <p className="text-ink-soft mt-2">
            Already a member?{" "}
            <Link to="/login" className="text-[var(--cm-primary)] underline underline-offset-4 font-semibold" data-testid="register-login-link">
              Sign in.
            </Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <Field label="Display name" testid="register-name-field">
              <input
                data-testid="register-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50"
                placeholder="Alex Chen"
              />
            </Field>
            <Field label="Username" testid="register-username-field">
              <div className="relative">
                <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  data-testid="register-username-input"
                  type="text"
                  autoComplete="username"
                  required
                  minLength={3}
                  maxLength={30}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-surface border border-ink-soft rounded-xl pl-10 pr-10 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50 lowercase"
                  placeholder="alex_chen"
                />
                {showUsernameHint && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2">
                    {usernameValid ? (
                      <Check className="h-4 w-4 text-[var(--cm-secondary)]" data-testid="register-username-valid" />
                    ) : (
                      <X className="h-4 w-4 text-red-500" data-testid="register-username-invalid" />
                    )}
                  </span>
                )}
              </div>
              <p className="text-xs text-ink-soft mt-1.5">
                3–30 chars: lowercase letters, numbers, underscores.
              </p>
            </Field>
            <Field label="Password" testid="register-password-field">
              <input
                data-testid="register-password-input"
                type="password"
                autoComplete="new-password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-surface border border-ink-soft rounded-xl px-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50"
                placeholder="At least 6 characters"
              />
            </Field>
            <Field label="Email" testid="register-email-field">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  data-testid="register-email-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-surface border border-ink-soft rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-[var(--cm-primary)]/50"
                  placeholder="you@example.com"
                />
              </div>
              <p className="text-xs text-ink-soft mt-1.5">
                We&apos;ll reach out about your shifts, verifications, and community updates. Never shared, never spammed.
              </p>
            </Field>
            {err && (
              <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2 rounded-lg">
                {err}
              </div>
            )}
            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading || !usernameValid}
              className="btn-primary w-full disabled:opacity-60"
            >
              {loading ? "Creating your account…" : "Create account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-widest font-semibold text-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
